import { ConfirmDialog } from "@/components/ConfirmDialog";
import { BooAvatars, DecidedLine, DepthTag, ExposureNote, GrantStages, LinkStatePill } from "@/components/grants/GrantBits";
import { ScopeEditor } from "@/components/grants/ScopeEditor";
import { Block, OverrideReason, REASON_MIN, SheetShell } from "@/components/grants/SheetShell";
import { type GrantActor, useInvalidateLink, useSourceGrantable } from "@/components/grants/useGrantActions";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { api, type BooLinkRow, type GrantScopes } from "@/lib/api";
import { countLine, isEmpty, labelsOf, linkState, presetOf, sameScopes } from "@/lib/grant-scopes";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

// What a source shares with one Boo, changeable at any time. Unchecking
// something stops it being shared from now on: the source refuses it at
// once, and the other Boo drops it on its next sync, which the sheet tracks.
export function ManageGrantSheet({ actor, link, onClose }: { actor: GrantActor; link: BooLinkRow; onClose: () => void }) {
  const granted = link.granted!;
  const source = link.source?.name ?? "this Boo";
  const consumer = link.consumer?.name ?? "the other Boo";
  const grantable = useSourceGrantable(actor, link);
  const [value, setValue] = useState<GrantScopes>(granted);
  const [reshare, setReshare] = useState(link.reshare);
  const [reason, setReason] = useState("");
  const [ending, setEnding] = useState(false);
  const invalidate = useInvalidateLink();
  const org = actor.kind === "org";
  const reasonOk = !org || reason.trim().length >= REASON_MIN;
  const changed = !sameScopes(value, granted) || reshare !== link.reshare;

  const save = useMutation({
    mutationFn: () =>
      org
        ? api.access.changeGrant(link.id, { scopes: value, reshare, reason: reason.trim() })
        : api.containers.connections.changeGrant(actor.containerId, link.id, { scopes: value, reshare }),
    onSuccess: (res) => {
      if (res.ended) toast.success(`${source} no longer shares with ${consumer}.`);
      else if (!res.complete) toast.success(`Saved. ${consumer} loses what you removed within a few minutes.`);
      else toast.success("Saved.");
      onClose();
    },
    onSettled: () => invalidate(link),
  });
  const end = useMutation({
    mutationFn: async () => {
      if (org) await api.access.end(link.id, reason.trim());
      else await api.containers.connections.end(actor.containerId, link.id);
    },
    onSuccess: () => {
      toast.success(`${source} no longer shares with ${consumer}.`);
      onClose();
    },
    onSettled: () => invalidate(link),
  });

  const g = grantable.data?.grantable;
  const busy = save.isPending || end.isPending;

  return (
    <SheetShell
      title={`What ${source} shares with ${consumer}`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" className="mr-auto text-destructive hover:text-destructive" onClick={() => setEnding(true)} disabled={busy || !reasonOk}>
            Stop sharing
          </Button>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={busy || !changed || !reasonOk}>
            {isEmpty(value) ? "Stop sharing" : "Save changes"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 rounded-lg border border-border p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <BooAvatars consumer={link.consumer} source={link.source} />
          <LinkStatePill state={linkState(link)} side="source" />
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <DepthTag depth={presetOf(value)} />
          <DecidedLine link={link} />
          {link.setUp && <span className="text-xs text-muted-foreground">{consumer} uses {countLine(link)}</span>}
        </div>
        {link.removalPendingSince && (
          <p className="text-xs text-sky-800 dark:text-sky-300">
            Stopping. {consumer} loses access within a few minutes.
          </p>
        )}
        <div className="border-t border-border pt-3">
          <GrantStages link={link} labels={labelsOf(g)} grantable={g} whole />
        </div>
      </div>

      <Block title="Shared">
        {grantable.isLoading ? (
          <Skeleton className="h-40 w-full rounded-lg" />
        ) : grantable.isError ? (
          <InlineError error={grantable.error} />
        ) : g ? (
          <ScopeEditor grantable={g} value={value} onChange={setValue} showTitles={!org} skillsAllOrNone={grantable.data?.scoped === false} disabled={busy} />
        ) : null}
        {grantable.data && !grantable.data.scoped && (
          <p className="text-xs text-muted-foreground">
            {source} is on an older version: it can share all of its skills or none, and channels and one-on-ones need an update.
          </p>
        )}
      </Block>

      <ExposureNote consumer={consumer} source={source} scopes={value} grantable={g} />

      <label className="flex cursor-pointer items-start justify-between gap-4 rounded-lg border border-border px-4 py-3">
        <span>
          <span className="block text-sm font-medium [overflow-wrap:anywhere]">Allow re-sharing</span>
          <span className="block text-xs text-muted-foreground [overflow-wrap:anywhere]">Boos that learn from {consumer} can get these memories too.</span>
        </span>
        <Switch checked={reshare} onCheckedChange={setReshare} disabled={busy} aria-label="Allow re-sharing" />
      </label>

      {org && <OverrideReason value={reason} onChange={setReason} boo={link.source?.name} />}
      {(save.isError || end.isError) && <InlineError error={save.error ?? end.error} />}

      <ConfirmDialog
        open={ending}
        onOpenChange={setEnding}
        title={`Stop sharing with ${consumer}?`}
        description="It loses these skills and memories within a few minutes."
        confirmLabel="Stop sharing"
        loading={end.isPending}
        onConfirm={() => end.mutate()}
      />
    </SheetShell>
  );
}
